import { Link, Outlet, createRootRoute, useRouterState } from '@tanstack/react-router';
import {
  Activity,
  Cloud,
  Globe,
  LinkIcon,
  ListChecks,
  LogOut,
  ReceiptText,
  Settings2,
  Shield,
} from 'lucide-react';
import { useToast } from '../components/Toaster';
import { Avatar } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { isAdminPathVisible } from '../lib/admin-environment';
import {
  API_ENVS,
  type ApiEnvId,
  getApiEnv,
  getUser,
  isProdApiEnv,
  setApiEnv,
  setToken,
} from '../lib/api';
import { cn } from '../lib/cn';

// prod 后端是 remote:链接 / 审计 / 域名归 DeepClick(无列表接口),仪表盘 / 黑白名单
// 直读共用 D1 只会显示 beta 陈旧数据 —— 这些页 prod 一律隐藏。唯一例外是「平台规则」:
// remote 引擎的基线规则就读当前 Prod Worker 独立 D1 的 rules:prod:v1 行，
// 再叠加租户上游的每链接 rulesOverrides，故 DC/RB 必须分别切换后管理。
const NAV = [
  { to: '/', label: '仪表盘', icon: Activity },
  { to: '/links', label: '链接管理', icon: LinkIcon },
  { to: '/billing', label: '账单', icon: ReceiptText },
  { to: '/domains', label: '域名管理', icon: Globe },
  { to: '/audits', label: '审计记录', icon: ListChecks },
  { to: '/rules', label: '平台规则', icon: Settings2, prodVisible: true },
  { to: '/blocklist', label: '黑白名单', icon: Shield },
];

export const Route = createRootRoute({
  component: RootLayout,
});

function RootLayout() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const toast = useToast();
  if (path === '/login' || path === '/auth/callback') return <Outlet />;
  const user = getUser();
  // prod 后端数据多归 DeepClick:只保留 prodVisible 的页(平台规则),其余显示托管提示。
  const currentEnv = getApiEnv();
  const isProd = isProdApiEnv(currentEnv);
  const nav = isProd ? NAV.filter((n) => n.prodVisible) : NAV;
  // 托管提示只在 Prod 生效:Beta 是 Smart Link 自营平台,每个页面都是它自己的数据。
  // 少了 isProd 守卫,Beta 下的详情页(/link/$id、/audit/$id —— 单数,不在
  // BETA_MANAGED_PATHS 的复数项里)会被整页替换成"由业务平台托管"而打不开。
  const showNotice = isProd && !isAdminPathVisible(currentEnv, path);
  return (
    <div className="flex min-h-dvh">
      <aside className="bg-muted/30 sticky top-0 hidden h-dvh w-56 shrink-0 border-r p-4 sm:flex sm:flex-col">
        <div className="mb-6 px-2 text-sm font-semibold tracking-tight">Smart Link 控制台</div>
        <nav className="flex flex-1 flex-col gap-1">
          {nav.map((n) => {
            const Icon = n.icon;
            const active =
              n.to === '/' ? path === '/' : path === n.to || path.startsWith(`${n.to}/`);
            return (
              <Link
                key={n.to}
                to={n.to}
                className={cn(
                  'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                  active
                    ? 'bg-accent text-accent-foreground font-medium'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )}
              >
                <Icon className="size-4" />
                {n.label}
              </Link>
            );
          })}
        </nav>
        <EnvSwitcher />
        {user && (
          <div className="mb-2 flex items-center gap-2 px-2">
            <Avatar src={user.avatar} name={user.name} className="size-7" />
            <div className="min-w-0">
              <div className="truncate text-sm font-medium" title={user.name}>
                {user.name}
              </div>
              <div className="text-muted-foreground truncate text-xs">
                {user.provider === 'feishu' ? '飞书' : '管理员'}
              </div>
            </div>
          </div>
        )}
        <Button
          variant="ghost"
          className="justify-start"
          onClick={() => {
            setToken(null);
            toast.info('已退出', '正在跳转登录页…');
            setTimeout(() => window.location.assign('/login'), 600);
          }}
        >
          <LogOut /> 退出登录
        </Button>
      </aside>
      <main className="min-w-0 flex-1 p-6">{showNotice ? <ProdManagedNotice /> : <Outlet />}</main>
    </div>
  );
}

/** prod 环境:链接 / 审计 / 域名由 DeepClick 平台管理,本后台仅保留「平台规则」配置。 */
function ProdManagedNotice() {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-4 pt-24 text-center">
      <div className="bg-muted text-muted-foreground flex size-14 items-center justify-center rounded-full">
        <Cloud className="size-7" />
      </div>
      <h1 className="text-lg font-semibold tracking-tight">该页由 DeepClick 平台管理</h1>
      <p className="text-muted-foreground text-sm leading-relaxed">
        生产环境的链接、审计日志与域名由对应业务平台(DC/RB)管理,本后台不展示这些数据。Prod
        下本后台仅保留 <span className="text-foreground font-medium">平台规则</span> 配置(影响当前
        Prod 入口链接的基线规则)。DC 与 RB 使用独立 Worker/D1，需在左下角分别切换。 如需管理 Beta
        数据,请切回 <span className="text-foreground font-medium">Beta</span> 环境。
      </p>
      <Button asChild>
        <Link to="/rules">前往平台规则</Link>
      </Button>
    </div>
  );
}

/** Beta 是 Smart Link 自有平台；Prod 再按 DC/RB 选择完全隔离的运行时。
 * 切换后整页刷新，让所有查询按新后端重取。 */
function EnvSwitcher() {
  const current = getApiEnv();
  function switchTo(id: ApiEnvId) {
    if (id === current) return;
    setApiEnv(id);
    window.location.reload();
  }
  const prod = isProdApiEnv(current);
  return (
    <div className="mb-3 px-2">
      <div className="text-muted-foreground mb-1 text-[11px] font-medium tracking-wide uppercase">
        数据环境
      </div>
      <div className="bg-muted/50 flex gap-0.5 rounded-md p-0.5">
        <button
          type="button"
          onClick={() => switchTo('beta')}
          className={cn(
            'flex-1 rounded px-2 py-1 text-xs font-medium transition-colors',
            !prod
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          Beta
        </button>
        <button
          type="button"
          onClick={() => switchTo(prod ? current : 'prod-dc')}
          className={cn(
            'flex-1 rounded px-2 py-1 text-xs font-medium transition-colors',
            prod ? 'bg-destructive text-white' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          Prod
        </button>
      </div>
      {prod && (
        <div className="mt-1 grid grid-cols-2 gap-1">
          {API_ENVS.filter((env) => env.id !== 'beta').map((env) => (
            <button
              key={env.id}
              type="button"
              onClick={() => switchTo(env.id)}
              className={cn(
                'rounded border px-2 py-1 text-[11px] font-medium transition-colors',
                env.id === current
                  ? 'border-destructive bg-destructive/10 text-destructive'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {env.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
